<template>
  <v-app>
    <v-no-ssr>
      <CartDrawer v-model="cartDrawer" />
    </v-no-ssr>

    <ProgressOverlay v-model="overlay" />
    <OffersButton
      v-if="Array.isArray(offers) && offers.length"
      @click="openOffers"
    />

    <!-- MFE navbar: portable shell + clicky-menu.css (.hda-mfe-*) -->
    <div class="hda-mfe-navbar">
      <div class="hda-mfe-navbar-inner">
        <a href="/"><NavHyperdriveLogo /></a>
        <div class="hda-mfe-nav-menu">
          <ClickyMenuNav :nav-items="navItems" />
          <!-- <a href="/contact" style="color: white; text-decoration: none"
            >Contact Us</a
          > -->
          <v-lazy>
            <v-btn
              v-if="hasCartSession"
              :icon="mdiCartArrowRight"
              aria-label="Open Cart"
              class="ma-2"
              color="light-blue-darken-2"
              @click="openCart"
            />
          </v-lazy>
        </div>
      </div>
    </div>

    <v-main :style="{ marginTop: `${-MENU_BAR_HEIGHT}px` }">
      <slot />

      <v-no-ssr>
        <CartReminderSnackbar
          v-model="cartReminderSnackbar"
          @continue="openCart"
          @dismiss="dismissCartReminder"
        />
      </v-no-ssr>

      <v-dialog
        v-model="offersDialog"
        scrollable
        transition="dialog-bottom-transition"
        width="auto"
      >
        <OffersCard @close="dismissOffers" />
      </v-dialog>

      <v-dialog v-model="videoDialog" width="auto">
        <VideoPlayer v-if="videoDialog && videoObject" :value="videoObject" />
      </v-dialog>
    </v-main>

    <v-footer v-if="siteConfig" color="blue-darken-4" theme="dark">
      <UnifiedFooterSection />
    </v-footer>
  </v-app>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue'
import { usePageContext } from '#root/renderer/usePageContext'
import { useProvideCartStore } from '#root/lib/useCartStore'
import { useProvideVariantsStore } from '#root/lib/useVariantsStore'
import { useProvideOccasionsStore } from '#root/lib/useOccasionsStore'
import { useCart } from '#root/lib/useCart'
import { useCartSession } from '#root/lib/useCartSession'
import { useOffersOrThrow, useProvideOffers } from '#root/lib/useOffers'
import { useEventBus } from '@vueuse/core'
import { MENU_BAR_HEIGHT } from '#root/lib/consts'
import { mdiCartArrowRight } from '@mdi/js'
import type { components } from '#root/types/directus'
import type { NavItem, NavListItem } from '#root/types/nav'

type ProductMenuItem = components['schemas']['ItemsMenuItemProducts']
type StaticPageMenuItem = components['schemas']['ItemsMenuItemStaticPage']
type ProductMenuEntry = { item: ProductMenuItem }
type StaticPageMenuEntry = { item: StaticPageMenuItem }
type ProductLink = {
  page_href?: string | null
  page_slug?: string | null
  name?: string | null
  occasion_type?: {
    code?: string | null
  } | null
}

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const isStaticPageMenuEntry = (
  menuItem: unknown
): menuItem is StaticPageMenuEntry => {
  if (!isObject(menuItem) || !('item' in menuItem) || !isObject(menuItem.item))
    return false
  if (!('title' in menuItem.item) || !('static_page' in menuItem.item))
    return false
  return true
}

const isProductMenuEntry = (
  menuItem: unknown
): menuItem is ProductMenuEntry => {
  if (!isObject(menuItem) || !('item' in menuItem) || !isObject(menuItem.item))
    return false
  if (!('title' in menuItem.item) || !('products' in menuItem.item))
    return false
  return true
}

const getStaticPageSlug = (menuItem: StaticPageMenuEntry): string | null => {
  const { static_page: staticPage } = menuItem.item
  if (!isObject(staticPage) || !('page_slug' in staticPage)) return null
  return typeof staticPage.page_slug === 'string' ? staticPage.page_slug : null
}

const getProductLink = (product: unknown): ProductLink | null => {
  if (
    !isObject(product) ||
    !('products_id' in product) ||
    !isObject(product.products_id)
  )
    return null
  return product.products_id
}

const {
  businessMenuItems,
  data: pageData,
  individualMenuItems,
  siteConfig,
  whatMenuItems
} = usePageContext()
const { hasCartSession } = useCartSession()
const isReady = ref(false)

useProvideVariantsStore()
useProvideOccasionsStore()
useProvideCartStore()

const { cartDrawer, cartReminderSnackbar, dismissCartReminder, openCart } =
  useCart({
    autoAddItemFromHash: !(
      isObject(pageData) &&
      'product' in pageData &&
      isObject(pageData.product)
    )
  })

const businessNavItems: NavItem[] = [
  {
    menuGroups: [
      ...(businessMenuItems.length
        ? [
            {
              listItems: businessMenuItems.flatMap(menuItem => {
                if (!isStaticPageMenuEntry(menuItem)) return []
                const pageSlug = getStaticPageSlug(menuItem)
                if (!pageSlug) return []
                return [
                  {
                    href: `/explore/${pageSlug}`,
                    title: menuItem.item.title || ''
                  }
                ]
              })
            }
          ]
        : []),
      {
        listItems: [
          {
            href: '/staffing',
            title: 'Staffing'
          }
        ]
      },
      {
        listItems: [
          {
            click: onClickParentItem,
            key: 'okrs',
            title: 'OKRs: Objectives and Key Results'
          },
          {
            href: '/okrs',
            parentKey: 'okrs',
            title: 'OKRs Overview'
          },
          {
            href: '/training/courses/okr',
            parentKey: 'okrs',
            title: 'OKR Certification Courses'
          },
          {
            href: '/assessments/do-i-need-okrs',
            parentKey: 'okrs',
            title: 'Assess Whether You Need OKRs'
          },
          {
            href: '/assessments/okr-health-check',
            parentKey: 'okrs',
            title: 'OKRs Health Check'
          },
          {
            href: 'https://www.scaledokrs.com/',
            parentKey: 'okrs',
            title: 'Scaled OKRs Site'
          }
        ]
      }
    ],
    title: 'Business',
    visible: 'md'
  }
]
const trainingNavItems: NavItem[] = [
  {
    href: '/training',
    menuGroups: [
      {
        listItems: [
          {
            href: '/training',
            title: 'Training Overview'
          },
          {
            href: '/training/schedule',
            title: 'Upcoming Training Schedule'
          },
          {
            href: '/training/agile-teams',
            title: 'Team Training'
          },
          {
            href: 'https://learn.hyperdriveagile.com/introduction-to-scaled-okrs',
            title: 'Self-Paced Learning: Introduction to Scaled OKRs'
          }
        ]
      },
      {
        listItems: [
          {
            href: '/training/courses',
            title: 'Certified Courses Overview'
          },
          {
            href: '/training/workshops',
            title: 'Workshops Overview'
          },
          {
            href: '/training/on-demand',
            title: 'On-Demand Overview'
          },
          {
            href: '/trainers',
            title: 'Meet the Trainers'
          }
        ]
      },
      {
        listItems: whatMenuItems.reduce<NavListItem[]>((items, menuItem) => {
          if (!isProductMenuEntry(menuItem)) return items
          const { item } = menuItem
          return [
            ...items,
            {
              click: onClickParentItem,
              key: String(item.id || ''),
              title: item.title || ''
            },
            ...(item.products || []).flatMap(product => {
              const productLink = getProductLink(product)
              if (!productLink) return []
              return [
                {
                  href: productLink.page_href
                    ? productLink.page_href
                    : productLink.occasion_type?.code === 'C'
                      ? `/training/courses/${productLink.page_slug}`
                      : productLink.occasion_type?.code === 'W'
                        ? `/training/workshops/${productLink.page_slug}`
                        : '',
                  parentKey: String(item.id || ''),
                  title: productLink.name || ''
                }
              ]
            })
          ]
        }, [])
      }
    ],
    title: 'Training',
    visible: 'md'
  }
]
const individualNavItems: NavItem[] = individualMenuItems.length
  ? [
      {
        menuGroups: [
          {
            listItems: individualMenuItems.flatMap(menuItem => {
              if (!isStaticPageMenuEntry(menuItem)) return []
              const pageSlug = getStaticPageSlug(menuItem)
              if (!pageSlug) return []
              return [
                {
                  href: `/explore/${pageSlug}`,
                  title: menuItem.item.title || ''
                }
              ]
            })
          }
        ],
        title: 'Individuals',
        visible: 'md'
      }
    ]
  : []
const resourcesNavItems: NavItem[] = [
  {
    href: '/resources',
    menuGroups: [
      {
        listItems: [
          {
            href: '/case-studies',
            title: 'Case Studies'
          },
          {
            href: 'https://explore.hyperdriveagile.com/goals-and-gamechangers',
            title: 'Podcast: Goals & Game Changers'
          },
          {
            href: '/faqs',
            title: 'FAQs'
          },
          {
            href: '/resources',
            title: 'All Resources (Articles, Downloads and Videos)'
          }
          // {
          //   click: () => openCookieSettings(),
          //   title: 'Manage Cookie Settings'
          // }
        ]
      }
    ],
    title: 'Resources',
    visible: 'lg'
  }
]
const navGroups = reactive<{ default: NavItem[] }>({
  default: [
    ...businessNavItems,
    ...trainingNavItems,
    ...individualNavItems,
    {
      href: '/events',
      title: 'Events',
      visible: 'lg'
    },
    ...resourcesNavItems,
    {
      href: '/about',
      title: 'About',
      visible: 'lg'
    }
  ]
})
const navItems = computed<NavItem[]>(() => navGroups.default)
const navSelectedParentKey = ref('')
const appBus = useEventBus<string>('app')
appBus.on((data: string) => {
  if (data === 'ready') {
    isReady.value = true
  }
})

const overlay = ref(false)
const overlayBus = useEventBus<boolean>('overlay')
overlayBus.on((show: boolean) => {
  overlay.value = show
})

const videoBus = useEventBus<unknown>('video')
videoBus.on((video: unknown) => {
  videoObject.value = video
  videoDialog.value = true
})
const videoDialog = ref(false)
const videoObject = ref<unknown>(null)

useProvideOffers({
  beforeOpen: () => {
    return !cartDrawer.value && !overlay.value && !videoDialog.value
  }
})
const { dismissOffers, offers, offersDialog, openOffers } = useOffersOrThrow()

function onClickParentItem(listItem: NavListItem) {
  const selectedKey = listItem.key || ''
  navSelectedParentKey.value =
    navSelectedParentKey.value === selectedKey ? '' : selectedKey
}
</script>

<style lang="scss">
@use '#root/styles/sticky-mixins.scss' as sticky;
</style>
